import { Injectable, computed, signal } from "@angular/core";
import type { AiChatCard } from "../utils/ai-chat-cards";
import {
  buildAskMessages,
  type AiChatOutgoingMessage,
  type AiChatSessionMessage,
} from "../utils/ai-chat-purify";

/**
 * AI 助手会话状态（内存态，**不落盘**）。
 *
 * 决策（spec §9.2.2 / §6.6）：聊天记录只存活在本次使用中——杀进程即清空，
 * 不写 localStorage / sessionStorage（PII 卡片原值不留在设备上）。
 *
 * 职责边界：只维护消息列表与"本轮请求消息"的组装；
 * 发送、额度、错误提示由页面（08）持有，保持本服务无 HTTP 依赖。
 */
@Injectable({ providedIn: "root" })
export class AiChatSessionService {
  private readonly state = signal<AiChatSessionMessage[]>([]);

  /** 当前会话消息（只读） */
  readonly messages = this.state.asReadonly();

  /** 是否还没有任何消息（页面用它决定是否显示空态示例） */
  readonly isEmpty = computed(() => this.state().length === 0);

  /**
   * 组装本轮请求消息（净化后的历史 + 本轮提问）。
   * 只读不写：先调用本方法，再 appendUser，避免提问被重复带入。
   */
  buildAsk(question: string): AiChatOutgoingMessage[] {
    return buildAskMessages(this.state(), question);
  }

  /** 追加用户提问（空文本忽略） */
  appendUser(content: string): void {
    const text = (content ?? "").trim();
    if (text.length === 0) return;
    this.state.update((messages) => [...messages, { role: "user", content: text }]);
  }

  /** 追加助手回答（文字 + 卡片；两者皆空则忽略） */
  appendAssistant(text: string, cards: readonly AiChatCard[] = []): void {
    const content = (text ?? "").trim();
    const kept = cards.filter((card) => card !== null && card !== undefined);
    if (content.length === 0 && kept.length === 0) return;
    this.state.update((messages) => [
      ...messages,
      { role: "assistant", content, cards: kept },
    ]);
  }

  /** 清空会话（杀进程外的唯一重置入口） */
  clear(): void {
    this.state.set([]);
  }
}
